import { Injectable } from "@angular/core";
import { catchError, map, Observable, of, throwError } from "rxjs";
import { ConnectionServices } from "../authentication/connection/connection.service";
import { IdentityService } from "../authentication/identity.service";
import { AbstractControl, ValidatorFn } from "@angular/forms";
import { environment } from "environments/environment";
import { ICreditCardPaymentData, IIdTypes, IMPInstallmentsRequest, IMPInstallmentsResponse, IMPPaymentMethodBin, IMPPaymentMethodId, IMPPaymentMethodResponse } from "./mp-api-interfaces";
// import * as es_ar from 'assets/i18n/es-ar.json';
import { AddCardCommand, IAddCardCommandParameters, IAddCardResponse } from "./command/AddCardCommand.command";
import { ICardDetail } from "../authentication/connection/interfaces/apiInterfaces";
import { IValidateCardHoldCommandParameters, ValidateCardHoldCommand } from "./command/ValidateCardHold.command";
import { CreditCardImg, CreditCardRegex, CreditCardType } from "../enum/credit-card.enum";

@Injectable()
export class MercadoPagoService {

  private DEFAULT_TOKEN_ERROR = 'toast.epayments.mercadopago.token_creation.error_code.default';
  private MERCADOPAGO_ERROR_HANDLER = catchError(err => {
    if (!err) {
      err = this.DEFAULT_TOKEN_ERROR;
    }
    // this._notificationsService.showInternalErrorMessage('toast.epayments.mercadopago.title', err);
    return of(err);
  });
  private DEFAULT_INSTALLMENTS_ERROR: 'toast.epayments.mercadopago.token_creation.error_code.default_installments_error';
  mp: any;

  constructor(
    // private _notificationsService: NotificationsService,
    private _connectionService: ConnectionServices,
    private _identityService: IdentityService
  ) {
  }

  /** Luhn algorithm validator for credit card field */
  luhnValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return (control.value && this.validateLuhn(control.value.replace(/\D/g, '')))
        ? null
        : {'invalidCreditCard': {value: control.value}};
    };
  }

  validateLuhn(creditCardNumber) {
    let sum = 0;
    for (let i = 0; i < creditCardNumber.length; i++) {
        let cardNum = parseInt(creditCardNumber[i]);
        if((creditCardNumber.length - i) % 2 === 0) {
          cardNum = cardNum * 2;
          if (cardNum > 9) {
            cardNum = cardNum - 9;
          }
        }
        sum += cardNum;
    }
    return (creditCardNumber && sum % 10 === 0) ? true : false;
  }

  /** Luhn algorithm validator for credit card expiration date field */
  static expiryDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const today = new Date();
      if (control.value) {
        if (control.value.length === 1 && Number(control.value) > 1) {
          return {'invalidExpiryDate': {value: control.value}}
        } else if (control.value.length === 2 && Number(control.value) > 12) {
          return {'invalidExpiryDate': {value: control.value}}
        } else if (control.value.length > 4) {
          if (control.value.toString().substr(0, 2) > 12) {
            return {'invalidExpiryDate': {value: control.value}}
          }
          const expiryDate = new Date(Number('20' + control.value.toString().substr(3, control.value.toString().length)),
            Number(control.value.toString().substr(0, 2)) + 1,
            0);
          if (today > expiryDate) {
            return {'invalidExpiryDate': {value: control.value}}
          }
        }
      } else {
        return null;
      }
    };
  }

  private getInstance(countryCode: string) {
    if(!!environment.mercadoPago[countryCode]){
      const mercadoPagoApiKey = environment.mercadoPago[countryCode].mercadoPagoApiKey;
      if (!this.mp) {
        this.mp = new MercadoPago(mercadoPagoApiKey);
      }
    }

    console.log('mp', this.mp);
    return this.mp;
  }

  loadScript(src: string) {
    return new Promise((resolve, reject) => {
      //load script
      const script: any = document.createElement('script');
      script.setAttribute('type', 'text/javascript')
      script.setAttribute('src', src);

      if (script.readyState) {  //IE
        script.onreadystatechange = () => {
          if (script.readyState === "loaded" || script.readyState === "complete") {
            script.onreadystatechange = null;
            resolve(true);
          }
        };

      } else {  //Others
        script.onload = resolve;
      }

      script.onerror = (_error: any) => reject();
      document.getElementsByTagName('head')[0].appendChild(script);

    });
  }

  public initMercadoPago(countryCode: string) {
    const th = this;
    this.loadScript('assets/mercadopago/V2.js').then(() => {
      th.getInstance(countryCode);
      console.log(this.mp);
    }).catch(e => console.log(e));
  }

  public getPaymentMethod(paymentMethodObj: IMPPaymentMethodBin | IMPPaymentMethodId, countryCode: string):
    Observable<IMPPaymentMethodResponse[]> {

    return new Observable<IMPPaymentMethodResponse[]>(observer => {
      this.getInstance(countryCode).getPaymentMethods(paymentMethodObj).then((resp) => {
        if (resp && resp.paging.total > 0) {
          observer.next(resp.results);
        } else {
          observer.next(null);
        }
        observer.complete();
      }).catch(error => {
        console.error('getPaymentMethods: ', error);
        observer.error(error);
      });
    }).pipe(this.MERCADOPAGO_ERROR_HANDLER);
  }

  public getInstallments(installmentsRequest: IMPInstallmentsRequest, countryCode: string): Observable<IMPInstallmentsResponse> {
    return new Observable<IMPInstallmentsResponse>(observer => {
      this.getInstance(countryCode).getInstallments({
        amount: installmentsRequest.amount.toString(),
        bin: installmentsRequest.bin
      }).then(resp => {
        if (resp) {
          observer.next(resp);
          observer.complete();
        } else {
          observer.error(this.getErrorFromJSON(resp[0]?.code?.toString(), this.DEFAULT_INSTALLMENTS_ERROR));
        }
      }).catch(error => {
        console.log('getInstallments: ', error);
        observer.error(this.getErrorFromJSON(error[0]?.code?.toString(), this.DEFAULT_INSTALLMENTS_ERROR));
      });
    }).pipe(this.MERCADOPAGO_ERROR_HANDLER);
  }

  public getIdTypes(countryCode: string): Observable<IIdTypes[]> {
    return new Observable<IIdTypes[]>(observer => {
      this.getInstance(countryCode).getIdentificationTypes().then(data => {
        if (data) {
          observer.next(data);
        } else {
          observer.next(null);
        }
        observer.complete();
      }).catch(error => {
        observer.error(error);
      });
    }).pipe(this.MERCADOPAGO_ERROR_HANDLER);
  }

  public clearSession (){
    // MercadoPago.clearSession();
  }

  public getMPToken(data: any, countryCode: string): Observable<any> {
    return new Observable<any>(observer => {
      this.getInstance(countryCode).createCardToken(data)
        .then((resp) => {
          if (resp) {
            observer.next(resp);
            observer.complete();
          } else {
            console.log('createCardToken: ', resp);
            observer.error(this.getErrorFromJSON(resp, this.DEFAULT_INSTALLMENTS_ERROR));
          }
        })
        .catch(error => {
          console.log('createCardToken: ', error);
          observer.error(this.getErrorFromJSON(error, this.DEFAULT_INSTALLMENTS_ERROR));
        });
    }).pipe(this.MERCADOPAGO_ERROR_HANDLER);
  }

  private getErrorFromJSON(code: string, defaultError: string): string {
    try {
      return;
      // return Object.keys((es_ar as any).toast.epayments.mercadopago.token_creation.error_code)
      //   .findIndex(x => x === code) > 0 ?
      //   'toast.epayments.mercadopago.token_creation.error_code.' + code
      //   : defaultError
    } catch(e) {
      return;
    }
  }

  addCard(passengerUserId : number, token: string, cardDetail: ICreditCardPaymentData): Observable<IAddCardResponse> {
    const command = new AddCardCommand();
    let card: ICardDetail= {
      token: token,
      cardNumber: (cardDetail.creditCardNumber)? cardDetail.creditCardNumber.toString():null,
      cvv: cardDetail.cvv,
      lastFourDigits: null,
      firstSixDigits: null,
      expirationYear: cardDetail.expiryDate.year.toString(),
      expirationMonth: cardDetail.expiryDate.month.toString(),
      issuerName: cardDetail.issuerName,
      paymentTypeId: null,
      paymentMethodId: cardDetail.issuerName,
      cardholderName: cardDetail.creditCardOwnerName,
      cardholderIdentificationNumber: cardDetail.creditCardOwnerId,
      cardholderIdentificationType: cardDetail.creditCardOwnerIdType
    }
    if(cardDetail.avsAddress && cardDetail.avsZipcode){
      card.address = cardDetail.avsAddress;
      card.zipCode = cardDetail.avsZipcode;
    }
    const request:IAddCardCommandParameters = {
      passengerUserId: passengerUserId,
      mercadopagoAppId: this._identityService.mercadoPagoAppId,
      token: token,
      cardDetail: card,
      carrierId: parseFloat(this._identityService.userId),
    }
    if(cardDetail.placeId){
      request.placeId = cardDetail.placeId;
    }
    command.setParameters(request);

    return this._connectionService
    .Request(command)
    .pipe(
      map(response => {
        return response as any;
      }),
      catchError(() => {
        // this._notificationsService.showHttpErrorMessage('toast.add_card.error.title', error);
        return throwError(() => false);
    }));
  }

  validateCard(passengerUserId: number, token: string, cardId?: number, cardDetail?: ICreditCardPaymentData) {
    const command = new ValidateCardHoldCommand();
    command.setParameters(<IValidateCardHoldCommandParameters>{
      passengerUserId: passengerUserId,
      token: token,
      cardId: cardId ? cardId : null,
      appId: this._identityService.mercadoPagoAppId,
      cardNumber: (cardDetail && cardDetail.creditCardNumber)? cardDetail.creditCardNumber.toString(): null,
      cvv: (cardDetail)? cardDetail.cvv: null,
      expirationYear: (cardDetail)? cardDetail.expiryDate.year: null,
      expirationMonth: (cardDetail)? cardDetail.expiryDate.month: null,
      issuerId: (cardDetail && cardDetail.bankId)? cardDetail.bankId.toString(): null,
      issuerName: (cardDetail)? cardDetail.issuerName: null,
      paymentTypeId: null,
      paymentMethodId: null,
      cardholderName: (cardDetail)? cardDetail.creditCardOwnerName: null,
      cardholderIdentificationNumber: (cardDetail)? cardDetail.creditCardOwnerId: null,
      cardholderIdentificationType: (cardDetail)? cardDetail.creditCardOwnerIdType: null,
      carrierId: this._identityService.userId,
    });

    return this._connectionService
    .Request(command)
    .pipe(
      map(response => {
        return response as any;
      }),
      catchError(() => {
        // this._notificationsService.showHttpErrorMessage('toast.add_card.error.title', error);
        return throwError(() => false);
    }));
  }

  getCardIcon(cardNumber: string): CreditCardImg {
    if (new RegExp(CreditCardRegex.VISA).test(cardNumber)) {
      return CreditCardImg.VISA;
    }
    else if (new RegExp(CreditCardRegex.MASTER).test(cardNumber)) {
      return CreditCardImg.MASTER;
    }
    else if (new RegExp(CreditCardRegex.AMEX).test(cardNumber)) {
      return CreditCardImg.AMEX;
    }
    else if (new RegExp(CreditCardRegex.DINERS).test(cardNumber)) {
      return CreditCardImg.DINERS;
    } else {
      return CreditCardImg.DEFAULT;
    }
  }

  getCardType(cardNumber: string): CreditCardType {
    if (new RegExp(CreditCardRegex.VISA).test(cardNumber)) {
      return CreditCardType.VISA;
    }
    else if (new RegExp(CreditCardRegex.MASTER).test(cardNumber)) {
      return CreditCardType.MASTER;
    }
    else if (new RegExp(CreditCardRegex.AMEX).test(cardNumber)) {
      return CreditCardType.AMEX;
    }
    else if (new RegExp(CreditCardRegex.DINERS).test(cardNumber)) {
      return CreditCardType.DINERS;
    } else {
      return null;
    }
  }
}
